export enum DaysStuckOp {
  GREATER_THAN = 'gt',
  LESS_THAN = 'lt',
  EQUALS = 'eq',
  BETWEEN = 'between'
}

export type DaysStuckFilterValue = {
  op: DaysStuckOp;
  value?: number;
  valueTo?: number;
};

export const DAYS_STUCK_OP_OPTIONS: {
  id: string;
  value: DaysStuckOp;
  label: string;
}[] = [
  { id: '1', value: DaysStuckOp.GREATER_THAN, label: 'greater than' },
  { id: '2', value: DaysStuckOp.LESS_THAN, label: 'less than' },
  { id: '3', value: DaysStuckOp.EQUALS, label: 'equals' },
  { id: '4', value: DaysStuckOp.BETWEEN, label: 'between' }
];

const OP_VALUES = new Set<string>(Object.values(DaysStuckOp));

export const isDaysStuckFilterComplete = (
  filter: DaysStuckFilterValue | null | undefined
): filter is DaysStuckFilterValue & { value: number } => {
  if (!filter?.op || filter.value == null || Number.isNaN(filter.value)) {
    return false;
  }
  if (filter.op === DaysStuckOp.BETWEEN) {
    return (
      filter.valueTo != null &&
      !Number.isNaN(filter.valueTo) &&
      filter.valueTo > filter.value
    );
  }
  return true;
};

export const isDaysStuckRangeInvalid = (
  filter: DaysStuckFilterValue | null | undefined
): boolean => {
  if (!filter || filter.op !== DaysStuckOp.BETWEEN) return false;
  if (filter.value == null || filter.valueTo == null) return false;
  if (Number.isNaN(filter.value) || Number.isNaN(filter.valueTo)) return false;
  return filter.valueTo <= filter.value;
};

export const encodeDaysStuckFilter = (
  filter: DaysStuckFilterValue | null | undefined
): string[] => {
  if (!filter?.op) return [];
  if (filter.value == null || Number.isNaN(filter.value)) {
    return [filter.op];
  }
  if (filter.op === DaysStuckOp.BETWEEN) {
    if (filter.valueTo == null || Number.isNaN(filter.valueTo)) {
      return [filter.op, String(filter.value)];
    }
    return [filter.op, String(filter.value), String(filter.valueTo)];
  }
  return [filter.op, String(filter.value)];
};

export const decodeDaysStuckFilter = (
  encoded?: string[]
): DaysStuckFilterValue | null => {
  if (!encoded?.length) return null;
  const [op, rawValue, rawValueTo] = encoded;
  if (!OP_VALUES.has(op)) return null;

  const filter: DaysStuckFilterValue = { op: op as DaysStuckOp };
  if (rawValue != null && rawValue !== '') {
    const value = Number(rawValue);
    if (!Number.isNaN(value)) filter.value = value;
  }
  if (op === DaysStuckOp.BETWEEN && rawValueTo != null && rawValueTo !== '') {
    const valueTo = Number(rawValueTo);
    if (!Number.isNaN(valueTo)) filter.valueTo = valueTo;
  }
  return filter;
};

export const MAX_DAYS_STUCK_DIGITS = 3;

export const MAX_DAYS_STUCK = 999;

export const clampDaysStuckInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, MAX_DAYS_STUCK_DIGITS);
  if (digits === '') return '';
  return String(Number(digits));
};

export const getDaysStuckSummary = (
  filter: DaysStuckFilterValue | null | undefined
): string | null => {
  if (!isDaysStuckFilterComplete(filter)) return null;

  const option = DAYS_STUCK_OP_OPTIONS.find((o) => o.value === filter.op);
  if (!option) return null;

  if (filter.op === DaysStuckOp.BETWEEN) {
    return `Filter leads stuck between ${filter.value} and ${filter.valueTo} days`;
  }

  return `Filter leads stuck ${option.label} ${filter.value} days`;
};

export const getDaysStuckSelectedCount = (values: string[]): number =>
  isDaysStuckFilterComplete(decodeDaysStuckFilter(values)) ? 1 : 0;
